import React from 'react'
import { Box, Card, Chip } from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { useTranslation } from 'react-i18next'
import WTypography from '../../../Components/whiteTypography'

/**
 * SimulationCompare
 * ------------------
 * Projection patrimoniale à 20 ans, courbes superposées :
 *   - trajectoire actuelle (sans action)
 *   - trajectoire avec plan d'action
 * Le delta est l'argument marketing massif : "sans rien faire, vous perdez X€".
 */
function SimulationCompare({ projection, formatMoney }) {
  const { t } = useTranslation('layout')
  // Devise du pays de l'utilisateur (injectée par la page). Repli sur le
  // formatage € local si non fournie.
  const money = formatMoney || formatEuro

  const merged = projection.actuel.map((p, i) => ({
    year: p.year,
    actuel: p.value,
    avecActions: projection.avecActions[i]?.value ?? null,
  }))

  const finalDelta = merged[merged.length - 1].avecActions - merged[merged.length - 1].actuel

  return (
    <Card
      sx={{
        background: '#0e1a36',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: 3,
        color: 'white',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <WTypography variant="overline" sx={{ color: '#a0b4d6 !important', letterSpacing: 1.5 }}>
            {t('conseillerPatrimonial.simulation.overline')}
          </WTypography>
          <WTypography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
            {t('conseillerPatrimonial.simulation.title')}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important', mt: 0.5 }}>
            {t('conseillerPatrimonial.simulation.subtitle')}
          </WTypography>
        </Box>

        <Chip
          icon={<TrendingUpIcon sx={{ color: 'inherit !important' }} />}
          label={t('conseillerPatrimonial.simulation.deltaLabel', { value: money(finalDelta) })}
          sx={{
            bgcolor: 'rgba(34,197,94,0.2)',
            color: '#4ade80',
            fontWeight: 700,
            fontSize: 13,
            height: 32,
            px: 1,
          }}
        />
      </Box>

      <Box sx={{ height: 320, mt: 2 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={merged} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="year" stroke="#a0b4d6" tick={{ fontSize: 11 }} />
            <YAxis
              stroke="#a0b4d6"
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => money(v)}
              width={70}
            />
            <Tooltip
              contentStyle={{
                background: '#0a1429',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
                color: '#dbe5f7',
              }}
              formatter={(v) => money(v)}
            />
            <Legend wrapperStyle={{ color: '#dbe5f7', fontSize: 12 }} iconType="circle" />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.3)" strokeDasharray="2 2" />
            <Line
              type="monotone"
              dataKey="actuel"
              name={t('conseillerPatrimonial.simulation.lineActuel')}
              stroke="#f87171"
              strokeWidth={2.5}
              strokeDasharray="6 4"
              dot={false}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="avecActions"
              name={t('conseillerPatrimonial.simulation.lineAvecPlan')}
              stroke="#4ade80"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, 1fr)' }} gap={2} mt={3}>
        <KPI
          label={t('conseillerPatrimonial.simulation.kpiActuel')}
          value={money(merged[merged.length - 1].actuel)}
          color="#f87171"
        />
        <KPI
          label={t('conseillerPatrimonial.simulation.kpiAvecPlan')}
          value={money(merged[merged.length - 1].avecActions)}
          color="#4ade80"
        />
        <KPI
          label={t('conseillerPatrimonial.simulation.kpiDelta')}
          value={`+${money(finalDelta)}`}
          color="#fbbf24"
          highlight
        />
      </Box>
    </Card>
  )
}

function KPI({ label, value, color, highlight }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: highlight ? `${color}15` : 'rgba(255,255,255,0.03)',
        border: `1px solid ${highlight ? color + '40' : 'rgba(255,255,255,0.06)'}`,
      }}
    >
      <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
        {label}
      </WTypography>
      <WTypography variant="h5" sx={{ fontWeight: 700, color: `${color} !important`, mt: 0.5 }}>
        {value}
      </WTypography>
    </Box>
  )
}

function formatEuro(n) {
  if (n === null || n === undefined) return '—'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${n < 0 ? '-' : ''}${(abs / 1_000_000).toFixed(1)}M€`
  if (abs >= 1_000) return `${n < 0 ? '-' : ''}${(abs / 1_000).toFixed(0)}k€`
  return `${n}€`
}

export default SimulationCompare
